import { useState, useEffect, useCallback, useRef } from 'react';
import {
  CalendarDays,
  Camera,
  Clock,
  ImagePlus,
  Pencil,
  Plus,
  Route,
  Trash2,
  UserRound,
  Wrench,
} from 'lucide-react';
import { useParams } from 'react-router-dom';
import Webcam from 'react-webcam';
import Lightbox from 'yet-another-react-lightbox';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/counter.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import {
  getNotesByMachine,
  createNote,
  updateNote,
  addNoteImages,
  deleteNote,
  deleteNoteImage,
  getNoteImage,
} from '../api/api';
import {
  processMultipleImages,
  createImagePreview,
  cleanupImagePreview,
  formatFileSize,
} from '../utils/imageUtils';
import { getUser } from '../utils/auth';
import useIsMobile from '../utils/useIsMobile';
import { ActionDrawer, ConfirmDialog, Toast } from '../components/ui';
import styles from './MachineNotes.module.scss';

function MachineNotes() {
  const { machineId: machineIdParam } = useParams();
  const isMobile = useIsMobile();
  const [notes, setNotes] = useState([]);
  const [machineId, setMachineId] = useState(machineIdParam ? parseInt(machineIdParam) : null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);

  // Create note state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [newNoteImages, setNewNoteImages] = useState([]);
  const [newNoteImagePreviews, setNewNoteImagePreviews] = useState([]);
  const [creating, setCreating] = useState(false);

  // Service note state (datetime-local values, in the browser's local time)
  const [isServiceNote, setIsServiceNote] = useState(false);
  const [serviceStartTime, setServiceStartTime] = useState('');
  const [serviceEndTime, setServiceEndTime] = useState('');
  const [travelDistanceKm, setTravelDistanceKm] = useState('');

  // Edit note state
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteContent, setEditNoteContent] = useState('');
  const [updating, setUpdating] = useState(false);

  // Add images state
  // Note currently receiving directly uploaded images (null when idle)
  const [addingImagesNoteId, setAddingImagesNoteId] = useState(null);

  // Image processing state
  const [imageProcessing, setImageProcessing] = useState(false);
  const [imageProcessingProgress, setImageProcessingProgress] = useState({ current: 0, total: 0 });

  // Image viewing state
  const [noteImageUrls, setNoteImageUrls] = useState({});
  const noteImageUrlsRef = useRef({});
  const newNoteImagePreviewsRef = useRef([]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  // Image gallery state
  const [showGallery, setShowGallery] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [currentNoteId, setCurrentNoteId] = useState(null);

  // Webcam state
  const [showWebcam, setShowWebcam] = useState(false);
  const webcamRef = useRef(null);
  const [pendingDeleteNote, setPendingDeleteNote] = useState(null);
  const [pendingImageDelete, setPendingImageDelete] = useState(null);
  const [success, setSuccess] = useState('');

  // Update machineId when route param changes
  useEffect(() => {
    if (machineIdParam) {
      setMachineId(parseInt(machineIdParam));
    }
  }, [machineIdParam]);

  const fetchNoteImageUrls = useCallback(async note => {
    // Load a note's images concurrently; the browser queues excess requests
    const urls = await Promise.all(
      note.images.map(async imageFilename => {
        try {
          const response = await getNoteImage(note.machine_id, note.id, imageFilename);
          return URL.createObjectURL(response.data);
        } catch (error) {
          console.error(`Failed to load image ${imageFilename} for note ${note.id}:`, error);
          return null;
        }
      })
    );
    return urls.filter(Boolean);
  }, []);

  const loadNotesImages = useCallback(
    async notesList => {
      const entries = await Promise.all(
        notesList
          .filter(note => note.images && note.images.length > 0)
          .map(async note => [note.id, await fetchNoteImageUrls(note)])
      );

      setNoteImageUrls(Object.fromEntries(entries));
    },
    [fetchNoteImageUrls]
  );

  const fetchNotes = useCallback(async () => {
    if (!machineId) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await getNotesByMachine(machineId);
      setNotes(response.data.notes);

      setNoteImageUrls(previousUrls => {
        Object.values(previousUrls).forEach(urls => {
          urls.forEach(cleanupImagePreview);
        });
        return {};
      });

      await loadNotesImages(response.data.notes);
    } catch (error) {
      console.error('Error fetching notes:', error);
      setError('Failed to fetch notes');
    } finally {
      setLoading(false);
    }
  }, [loadNotesImages, machineId]);

  const loadSingleNoteImages = async note => {
    if (!note.images || note.images.length === 0) {
      // Clean up existing images for this note
      const existingUrls = noteImageUrls[note.id];
      if (existingUrls) {
        existingUrls.forEach(cleanupImagePreview);
        setNoteImageUrls(prev => ({
          ...prev,
          [note.id]: [],
        }));
      }
      return;
    }

    const imageUrls = await fetchNoteImageUrls(note);

    // Clean up old URLs for this note before setting new ones
    const existingUrls = noteImageUrls[note.id];
    if (existingUrls) {
      existingUrls.forEach(cleanupImagePreview);
    }

    setNoteImageUrls(prev => ({
      ...prev,
      [note.id]: imageUrls,
    }));
  };

  useEffect(() => {
    // Get current user information
    const user = getUser();
    setCurrentUser(user);
  }, []);

  useEffect(() => {
    if (machineId) {
      fetchNotes();
    }
  }, [machineId, fetchNotes]);

  useEffect(() => {
    noteImageUrlsRef.current = noteImageUrls;
  }, [noteImageUrls]);

  useEffect(() => {
    newNoteImagePreviewsRef.current = newNoteImagePreviews;
  }, [newNoteImagePreviews]);

  // Cleanup image URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(noteImageUrlsRef.current).forEach(urls => {
        urls.forEach(cleanupImagePreview);
      });
      newNoteImagePreviewsRef.current.forEach(cleanupImagePreview);
    };
  }, []);

  const resetServiceFields = () => {
    setIsServiceNote(false);
    setServiceStartTime('');
    setServiceEndTime('');
    setTravelDistanceKm('');
  };

  const validateServiceFields = () => {
    if (!serviceStartTime || !serviceEndTime) {
      return 'Please select the service start and end time';
    }
    if (new Date(serviceEndTime) < new Date(serviceStartTime)) {
      return 'Service end time must not be earlier than start time';
    }
    const distance = Number(travelDistanceKm);
    if (travelDistanceKm === '' || Number.isNaN(distance) || distance < 0) {
      return 'Please enter a valid travel distance';
    }
    return '';
  };

  const handleCreateNote = async () => {
    if (!newNoteContent.trim()) {
      setError('Please enter note content');
      return;
    }

    if (isServiceNote) {
      const serviceError = validateServiceFields();
      if (serviceError) {
        setError(serviceError);
        return;
      }
    }

    try {
      setCreating(true);
      setError('');

      const formData = new FormData();
      formData.append('machine_id', machineId);
      formData.append('content', newNoteContent);

      if (isServiceNote) {
        formData.append('is_service_note', 'true');
        // datetime-local is local time; send as UTC
        formData.append('service_start_time', new Date(serviceStartTime).toISOString());
        formData.append('service_end_time', new Date(serviceEndTime).toISOString());
        formData.append('travel_distance_km', Number(travelDistanceKm));
      }

      // Add processed images
      newNoteImages.forEach(image => {
        formData.append('images', image);
      });

      await createNote(formData);

      // Reset form
      setNewNoteContent('');
      resetServiceFields();
      setNewNoteImages([]);
      newNoteImagePreviews.forEach(cleanupImagePreview);
      setNewNoteImagePreviews([]);
      setShowCreateForm(false);

      // Refresh notes
      fetchNotes();
    } catch (error) {
      console.error('Error creating note:', error);
      setError('Failed to create note');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateNote = async noteId => {
    if (!editNoteContent.trim()) {
      setError('Please enter note content');
      return;
    }

    try {
      setUpdating(true);
      setError('');

      await updateNote(noteId, editNoteContent);

      // Reset edit state
      setEditingNoteId(null);
      setEditNoteContent('');

      // Refresh notes
      fetchNotes();
    } catch (error) {
      console.error('Error updating note:', error);
      setError('Failed to update note');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteNote = async noteId => {
    try {
      await deleteNote(noteId);
      setPendingDeleteNote(null);
      setSuccess('Note deleted.');
      fetchNotes();
    } catch (error) {
      console.error('Error deleting note:', error);
      setError('Failed to delete note');
    }
  };

  const handleImageUpload = useCallback(
    async (files, isAdd = false) => {
      if (!files || files.length === 0) {
        return;
      }

      // Check total images limit including existing ones
      const currentImageCount = newNoteImages.length;
      if (currentImageCount + files.length > 30) {
        setError(
          `Maximum 30 images allowed per note. You currently have ${currentImageCount} images. You can add ${30 - currentImageCount} more.`
        );
        return;
      }

      try {
        setImageProcessing(true);
        setError('');
        // Set initial progress with correct total
        setImageProcessingProgress({ current: 0, total: files.length });

        const processedImages = await processMultipleImages(
          Array.from(files),
          500, // 500KB max size
          (current, total) => {
            setImageProcessingProgress({ current, total });
          }
        );

        if (processedImages.length === 0) {
          setError('No valid images were processed');
          return;
        }

        // Create preview URLs for new note creation only
        if (!isAdd) {
          const previews = processedImages.map(createImagePreview);
          // Append to existing images instead of replacing
          setNewNoteImages(prev => [...prev, ...processedImages]);
          setNewNoteImagePreviews(prev => [...prev, ...previews]);
        }
      } catch (error) {
        console.error('Error processing images:', error);
        setError('Failed to process images');
      } finally {
        setImageProcessing(false);
        setImageProcessingProgress({ current: 0, total: 0 });
      }
    },
    [newNoteImages.length]
  );

  const handleDirectImageUpload = async (noteId, files) => {
    if (!files || files.length === 0) {
      return;
    }

    const existingCount = notes.find(n => n.id === noteId)?.images?.length || 0;
    if (existingCount + files.length > 30) {
      const remaining = Math.max(0, 30 - existingCount);
      setError(`Maximum 30 images allowed per note. You can add ${remaining} more.`);
      return;
    }

    try {
      setAddingImagesNoteId(noteId);
      setError('');

      // Process images (compress, convert to JPG)
      setImageProcessing(true);
      // Set initial progress with correct total
      setImageProcessingProgress({ current: 0, total: files.length });

      const processedImages = await processMultipleImages(
        Array.from(files),
        500, // 500KB max size
        (current, total) => {
          setImageProcessingProgress({ current, total });
        }
      );
      setImageProcessing(false);

      if (processedImages.length === 0) {
        setError('No valid images were processed');
        return;
      }

      // Upload images directly
      const formData = new FormData();
      processedImages.forEach(image => {
        formData.append('images', image);
      });

      const response = await addNoteImages(noteId, formData);
      const updatedNote = response.data;

      // Update the specific note with new images
      const updatedNotes = notes.map(note => {
        if (note.id === noteId) {
          return { ...note, images: updatedNote.images };
        }
        return note;
      });
      setNotes(updatedNotes);

      // Load images for the updated note only
      await loadSingleNoteImages(updatedNote);
    } catch (error) {
      console.error('Error adding images:', error);
      setError('Failed to add images');
    } finally {
      setAddingImagesNoteId(null);
      setImageProcessing(false);
      setImageProcessingProgress({ current: 0, total: 0 });
    }
  };

  const handleStartEdit = note => {
    setEditingNoteId(note.id);
    setEditNoteContent(note.content);
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditNoteContent('');
  };

  // Trigger a hidden file input; with capture=true mobile browsers open the
  // native camera app instead of the gallery picker
  const openImagePicker = (onFiles, { capture = false } = {}) => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    if (capture) {
      fileInput.capture = 'environment';
    } else {
      fileInput.multiple = true;
    }
    fileInput.onchange = async event => {
      const files = event.target.files;
      if (files && files.length > 0) {
        await onFiles(files);
      }
    };

    fileInput.click();
  };

  const handleStartAddImages = (noteId, useCamera = false) => {
    openImagePicker(files => handleDirectImageUpload(noteId, files), { capture: useCamera });
  };

  const handleCancelCreate = () => {
    setShowCreateForm(false);
    setNewNoteContent('');
    resetServiceFields();
    setNewNoteImages([]);
    newNoteImagePreviews.forEach(cleanupImagePreview);
    setNewNoteImagePreviews([]);
    setShowWebcam(false);
  };

  const handleDeletePreviewImage = index => {
    // Clean up the preview URL
    if (newNoteImagePreviews[index]) {
      cleanupImagePreview(newNoteImagePreviews[index]);
    }
    // Remove the image and preview at the specified index
    setNewNoteImages(prev => prev.filter((_, i) => i !== index));
    setNewNoteImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Webcam related functions
  const handleOpenWebcam = () => {
    setShowWebcam(true);
  };

  const handleCloseWebcam = () => {
    setShowWebcam(false);
  };

  const handleTakePhoto = useCallback(async () => {
    if (!webcamRef.current) {
      return;
    }

    try {
      const imageSrc = webcamRef.current.getScreenshot();
      if (!imageSrc) {
        setError('Failed to capture photo');
        return;
      }

      // Convert base64 to blob
      const response = await fetch(imageSrc);
      const blob = await response.blob();
      // Create a File object from the blob
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const file = new File([blob], `webcam-capture-${timestamp}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });

      // Add to the existing image upload process
      await handleImageUpload([file], false);
    } catch (error) {
      console.error('Error capturing photo:', error);
      setError('Failed to capture photo');
    }
  }, [handleImageUpload]);

  const handleDeleteSingleImage = async (noteId, imageFilename) => {
    try {
      await deleteNoteImage(noteId, imageFilename);
      setPendingImageDelete(null);

      // Update the specific note instead of fetching all notes
      const updatedNotes = notes.map(note => {
        if (note.id === noteId) {
          // Remove the deleted image from the note's images array
          const updatedImages = note.images.filter(img => img !== imageFilename);
          return { ...note, images: updatedImages };
        }
        return note;
      });
      setNotes(updatedNotes);

      // Clean up the image URL for the deleted image and update noteImageUrls
      const currentImageUrls = noteImageUrls[noteId] || [];
      const imageIndex = notes.find(n => n.id === noteId)?.images?.indexOf(imageFilename);
      if (imageIndex !== undefined && imageIndex !== -1 && currentImageUrls[imageIndex]) {
        cleanupImagePreview(currentImageUrls[imageIndex]);
        const updatedImageUrls = currentImageUrls.filter((_, index) => index !== imageIndex);
        setNoteImageUrls(prev => ({
          ...prev,
          [noteId]: updatedImageUrls,
        }));
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      setError('Failed to delete image');
    }
  };

  const handleUploadImagesClick = () => {
    openImagePicker(files => handleImageUpload(files, false));
  };

  const handleNativeCameraCapture = () => {
    openImagePicker(files => handleImageUpload(files, false), { capture: true });
  };

  const handleOpenGallery = (noteId, startIndex) => {
    const imageUrls = noteImageUrls[noteId] || [];
    setGalleryImages(imageUrls.map(url => ({ src: url })));
    setCurrentSlideIndex(startIndex);
    setCurrentNoteId(noteId);
    setShowGallery(true);
  };

  const handleCloseGallery = () => {
    setShowGallery(false);
    setGalleryImages([]);
    setCurrentSlideIndex(0);
    setCurrentNoteId(null);
  };

  const renderGalleryDeleteButton = () => {
    const note = notes.find(n => n.id === currentNoteId);
    if (!note || !canEditNote(note) || editingNoteId !== note.id) {
      return null;
    }

    const imageFilename = note.images && note.images[currentSlideIndex];

    return (
      <button
        key="delete"
        type="button"
        className={styles.galleryDeleteButton}
        onClick={() => {
          handleCloseGallery();
          if (imageFilename) {
            setPendingImageDelete({ noteId: currentNoteId, imageFilename });
          }
        }}
        title="Delete this image"
      >
        🗑️ Delete
      </button>
    );
  };

  const canEditNote = note => {
    if (!currentUser || !note) {
      return false;
    }
    // User can edit their own notes or admin can edit any note
    return note.owner_id === currentUser.id || currentUser.role === 'admin';
  };

  const formatDate = dateString => {
    return new Date(dateString).toLocaleString();
  };

  // Service times are minute-precision UTC; show them in local time
  const formatServiceTime = dateString => {
    return new Date(dateString).toLocaleString(undefined, {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  };

  const formatServiceDuration = (startString, endString) => {
    const totalMinutes = Math.round((new Date(endString) - new Date(startString)) / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours === 0) {
      return `${minutes} min`;
    }
    return minutes === 0 ? `${hours} h` : `${hours} h ${minutes} min`;
  };

  return (
    <div className={styles.page}>
      {/* Image lightbox: swipe to navigate, pinch/double-tap/scroll to zoom */}
      <Lightbox
        open={showGallery}
        close={handleCloseGallery}
        slides={galleryImages}
        index={currentSlideIndex}
        on={{ view: ({ index }) => setCurrentSlideIndex(index) }}
        plugins={isMobile ? [Zoom, Counter] : [Zoom, Counter, Thumbnails]}
        zoom={{ maxZoomPixelRatio: 4, scrollToZoom: true }}
        controller={{ closeOnPullDown: true, closeOnBackdropClick: true }}
        toolbar={{
          buttons: [renderGalleryDeleteButton(), 'close'].filter(Boolean),
        }}
      />

      <header className={styles.pageHeader}>
        <div>
          <span className={styles.eyebrow}>Service records</span>
          <h1>Machine notes</h1>
          <p>Keep field observations, maintenance context, and photos in a chronological log.</p>
        </div>
        <button className={styles.createNoteButton} onClick={() => setShowCreateForm(true)}>
          <Plus size={16} /> New note
        </button>
      </header>

      {error && (
        <Toast tone="error" onClose={() => setError('')}>
          {error}
        </Toast>
      )}
      {success && (
        <Toast tone="success" onClose={() => setSuccess('')}>
          {success}
        </Toast>
      )}

      <ConfirmDialog
        open={!!pendingDeleteNote}
        title="Delete this note?"
        description="The note will be permanently removed. This cannot be undone."
        confirmLabel="Delete note"
        onConfirm={() => handleDeleteNote(pendingDeleteNote)}
        onCancel={() => setPendingDeleteNote(null)}
      />
      <ConfirmDialog
        open={!!pendingImageDelete}
        title="Remove this image from the note?"
        confirmLabel="Remove image"
        onConfirm={() =>
          handleDeleteSingleImage(pendingImageDelete.noteId, pendingImageDelete.imageFilename)
        }
        onCancel={() => setPendingImageDelete(null)}
      />

      {!machineId ? (
        <div className={styles.noDeviceSelected}>Please select a machine to view its notes.</div>
      ) : (
        <div className={styles.notesSection}>
          <ActionDrawer
            open={showCreateForm}
            eyebrow="Machine notes"
            title="Create a field note"
            description="Add the observation first, then attach photos if they help explain it."
            onClose={handleCancelCreate}
            closeDisabled={creating}
            footer={
              <>
                <button
                  className={styles.drawerSecondaryButton}
                  onClick={handleCancelCreate}
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  className={styles.drawerPrimaryButton}
                  onClick={handleCreateNote}
                  disabled={creating || imageProcessing}
                >
                  {creating ? 'Creating…' : 'Create note'}
                </button>
              </>
            }
          >
            <div className={styles.createNoteForm}>
              <div className={styles.formContent}>
                <div className={styles.formGroup}>
                  <label htmlFor="newNoteContent">Observation</label>
                  <textarea
                    id="newNoteContent"
                    className={styles.noteTextarea}
                    value={newNoteContent}
                    onChange={e => setNewNoteContent(e.target.value)}
                    placeholder="What did you observe or change?"
                    rows={7}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.checkboxLabel} htmlFor="isServiceNote">
                    <input
                      id="isServiceNote"
                      type="checkbox"
                      checked={isServiceNote}
                      onChange={e => setIsServiceNote(e.target.checked)}
                    />
                    Service note
                  </label>

                  {isServiceNote && (
                    <div className={styles.serviceFields}>
                      <div className={styles.serviceField}>
                        <label htmlFor="serviceStartTime">Service start</label>
                        <input
                          id="serviceStartTime"
                          type="datetime-local"
                          value={serviceStartTime}
                          max={serviceEndTime || undefined}
                          onChange={e => setServiceStartTime(e.target.value)}
                        />
                      </div>
                      <div className={styles.serviceField}>
                        <label htmlFor="serviceEndTime">Service end</label>
                        <input
                          id="serviceEndTime"
                          type="datetime-local"
                          value={serviceEndTime}
                          min={serviceStartTime || undefined}
                          onChange={e => setServiceEndTime(e.target.value)}
                        />
                      </div>
                      <div className={styles.serviceField}>
                        <label htmlFor="travelDistanceKm">Travel distance (km)</label>
                        <input
                          id="travelDistanceKm"
                          type="number"
                          inputMode="decimal"
                          min="0"
                          step="0.1"
                          value={travelDistanceKm}
                          onChange={e => setTravelDistanceKm(e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label>
                    Attachments <small>{newNoteImagePreviews.length}/30 images</small>
                  </label>
                  <div className={styles.imageInputContainer}>
                    {isMobile ? (
                      <button
                        type="button"
                        className={styles.cameraButton}
                        onClick={handleNativeCameraCapture}
                      >
                        <Camera size={15} /> Take photo
                      </button>
                    ) : (
                      !showWebcam && (
                        <button
                          type="button"
                          className={styles.cameraButton}
                          onClick={handleOpenWebcam}
                        >
                          <Camera size={15} /> Use camera
                        </button>
                      )
                    )}
                    <button
                      type="button"
                      className={styles.cameraButton}
                      onClick={handleUploadImagesClick}
                    >
                      <ImagePlus size={15} /> Choose images
                    </button>
                  </div>
                </div>

                {imageProcessing && (
                  <div className={styles.processingStatus}>
                    Processing images... {imageProcessingProgress.current}/
                    {imageProcessingProgress.total}
                  </div>
                )}

                {showWebcam && (
                  <div className={styles.webcamContainer}>
                    <div className={styles.webcamPreview}>
                      <Webcam
                        ref={webcamRef}
                        audio={false}
                        height={300}
                        width={400}
                        screenshotFormat="image/jpeg"
                        className={styles.webcam}
                      />
                    </div>
                    <div className={styles.webcamControls}>
                      <button
                        type="button"
                        className={styles.takePhotoButton}
                        onClick={handleTakePhoto}
                        disabled={imageProcessing}
                      >
                        Take Photo
                      </button>
                      <button
                        type="button"
                        className={styles.closeWebcamButton}
                        onClick={handleCloseWebcam}
                      >
                        Close Camera
                      </button>
                    </div>
                  </div>
                )}

                {newNoteImagePreviews.length > 0 && (
                  <div className={styles.imagePreviews}>
                    <h4>Ready to attach</h4>
                    <div className={styles.previewGrid}>
                      {newNoteImagePreviews.map((preview, index) => (
                        <div key={index} className={styles.previewItemWithDelete}>
                          <img src={preview} alt={`Preview ${index + 1}`} />
                          <div className={styles.imageInfo}>
                            {formatFileSize(newNoteImages[index]?.size || 0)}
                          </div>
                          <button
                            className={styles.deletePreviewButton}
                            onClick={() => handleDeletePreviewImage(index)}
                            title="Delete this image"
                            type="button"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </ActionDrawer>

          {/* Notes List Section */}
          <div className={styles.notesListSection}>
            <div className={styles.listHeader}>
              <div>
                <span>Chronological log</span>
                <h2>Notes</h2>
              </div>
              <b>{notes.length}</b>
            </div>

            {loading ? (
              <div className={styles.loading}>Loading notes...</div>
            ) : notes.length === 0 ? (
              <div className={styles.noNotes}>No notes found for this device.</div>
            ) : (
              <div className={styles.notesList}>
                {notes.map(note => (
                  <div key={note.id} className={styles.noteItem}>
                    <div className={styles.noteHeader}>
                      <div className={styles.noteMeta}>
                        <span className={styles.noteDate}>
                          <CalendarDays size={13} /> {formatDate(note.create_at)}
                        </span>
                        <span className={styles.noteOwner}>
                          <UserRound size={13} /> {note.owner_username}
                        </span>
                        {note.is_service_note && (
                          <span className={styles.serviceBadge}>
                            <Wrench size={12} /> Service
                          </span>
                        )}
                      </div>

                      <div className={styles.noteActions}>
                        {editingNoteId === note.id ? (
                          <>
                            <button
                              className={styles.saveButton}
                              onClick={() => handleUpdateNote(note.id)}
                              disabled={updating}
                            >
                              {updating ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              className={styles.cancelButton}
                              onClick={handleCancelEdit}
                              disabled={updating}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          canEditNote(note) && (
                            <>
                              <button
                                className={styles.editButton}
                                onClick={() => handleStartEdit(note)}
                              >
                                <Pencil size={14} /> Edit
                              </button>
                              <button
                                className={styles.deleteButton}
                                onClick={() => setPendingDeleteNote(note.id)}
                              >
                                <Trash2 size={14} /> Delete
                              </button>
                            </>
                          )
                        )}
                      </div>
                    </div>

                    {note.is_service_note && (
                      <div className={styles.serviceInfo}>
                        {note.service_start_time && note.service_end_time && (
                          <span>
                            <Clock size={13} /> {formatServiceTime(note.service_start_time)} –{' '}
                            {formatServiceTime(note.service_end_time)} (
                            {formatServiceDuration(note.service_start_time, note.service_end_time)})
                          </span>
                        )}
                        {note.travel_distance_km !== null &&
                          note.travel_distance_km !== undefined && (
                            <span>
                              <Route size={13} /> {note.travel_distance_km} km
                            </span>
                          )}
                      </div>
                    )}

                    {editingNoteId === note.id ? (
                      <div className={styles.editForm}>
                        <div className={styles.formGroup}>
                          <label>Edit Content:</label>
                          <textarea
                            className={styles.noteTextarea}
                            value={editNoteContent}
                            onChange={e => setEditNoteContent(e.target.value)}
                            rows={4}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className={styles.noteContent}>{note.content}</div>
                    )}

                    {/* Images section - only show if has images, in edit mode, or processing */}
                    {((note.images && note.images.length > 0) ||
                      (canEditNote(note) && editingNoteId === note.id) ||
                      addingImagesNoteId === note.id) && (
                      <div className={styles.noteImages}>
                        <div className={styles.imagesHeader}>
                          <h4>Images:</h4>
                          {canEditNote(note) && editingNoteId === note.id && (
                            <div className={styles.imagesHeaderButtons}>
                              {isMobile && (
                                <button
                                  className={styles.addPicturesButton}
                                  onClick={() => handleStartAddImages(note.id, true)}
                                >
                                  Take Photo
                                </button>
                              )}
                              <button
                                className={styles.addPicturesButton}
                                onClick={() => handleStartAddImages(note.id)}
                              >
                                Add Pictures...
                              </button>
                            </div>
                          )}
                        </div>

                        {addingImagesNoteId === note.id && (
                          <div className={styles.processingStatus}>
                            {imageProcessing
                              ? `Processing images... ${imageProcessingProgress.current}/${imageProcessingProgress.total}`
                              : 'Adding images...'}
                          </div>
                        )}

                        {note.images && note.images.length > 0 && noteImageUrls[note.id] && (
                          <div className={styles.imageGrid}>
                            {noteImageUrls[note.id].map((imageUrl, index) => {
                              const imageFilename = note.images && note.images[index];
                              return (
                                <div key={index} className={styles.imageItemWithDelete}>
                                  <img
                                    src={imageUrl}
                                    alt={`Note attachment ${index + 1}`}
                                    onClick={() => handleOpenGallery(note.id, index)}
                                  />
                                  {imageFilename &&
                                    canEditNote(note) &&
                                    editingNoteId === note.id && (
                                      <button
                                        className={styles.deleteImageButton}
                                        onClick={e => {
                                          e.stopPropagation();
                                          setPendingImageDelete({
                                            noteId: note.id,
                                            imageFilename,
                                          });
                                        }}
                                        title="Delete this image"
                                      >
                                        ×
                                      </button>
                                    )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {(!note.images || note.images.length === 0) &&
                          canEditNote(note) &&
                          editingNoteId === note.id && (
                            <p className={styles.noImages}>No images yet.</p>
                          )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default MachineNotes;
